// Standalone CreatorStore customize landing page — for offers not connected to a campaign.
// Uses Firebase auth (via memberId) instead of the offer URL JWT.

import { LandingPageEditor } from "@/components/landing-page/landing-page-editor";
import { ProjectPageHeader } from "@/components/projects/project-page-header";
import { LandingPageProvider } from "@/lib/contexts/landing-page-context";
import {
  isPromoOffer,
  useAffiliateOfferByMemberQuery,
} from "@/lib/queries/promo-codes";
import { useProfileQuery } from "@/lib/queries/profile";
import { Skeleton } from "@repo/ui";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { z } from "zod";
import type { OrgRouteContext } from "@/lib/types/route-context";

const searchSchema = z.object({
  memberId: z.coerce.number().int().positive(),
});

export const Route = createFileRoute(
  "/(org)/o/$orgSlug/offers/$offerId/customize-landing",
)({
  component: StandaloneCustomizeLandingPage,
  validateSearch: searchSchema,
});

function StandaloneCustomizeLandingPage() {
  const { offerId, orgSlug } = Route.useParams();
  const { memberId } = Route.useSearch();
  const context = Route.useRouteContext() as OrgRouteContext;
  const { organization } = context;

  const numericOfferId = Number(offerId);
  const isValidOfferId = Number.isFinite(numericOfferId) && numericOfferId > 0;

  const {
    data: offerData,
    isPending,
    error,
  } = useAffiliateOfferByMemberQuery(
    organization.id,
    memberId,
    isValidOfferId ? numericOfferId : 0,
  );

  // Extract profileId early for hooks call (use empty string as fallback for hook call consistency)
  const profileId = offerData?.profileId || "";

  // Fetch actual creator profile data for accurate name display
  // Called unconditionally to satisfy React hooks rules (even if profileId is empty)
  const { data: profileData } = useProfileQuery(organization.id, profileId);

  if (!isValidOfferId) {
    throw notFound();
  }

  if (isPending) {
    return <LoadingSkeleton />;
  }

  if (error) {
    // Re-throw auth/redirect Responses so the app-level AuthErrorBoundary handles them
    if (error instanceof Response) throw error;

    return (
      <div className="p-6">
        <ProjectPageHeader title="Customize Landing Page" />
        <div className="mt-4 py-8 text-center">
          <p className="text-destructive">
            Failed to load landing page data. Please try again.
          </p>
        </div>
      </div>
    );
  }

  const offer = offerData?.offer;
  const offerUrl = offerData?.offerUrl;

  if (
    !offer ||
    !isPromoOffer(offer) ||
    !offerUrl ||
    !profileId ||
    !offer.clientId ||
    !offer.offerId ||
    offer.affiliateOfferPromoId <= 0
  ) {
    throw notFound();
  }

  // Construct full creator name from profile data
  // Priority: profile.name > firstName + lastName > offer.memberName > "Creator"
  // Normalize all candidates to avoid whitespace-only strings bypassing fallback
  const creatorName = profileData
    ? profileData.name?.trim() ||
      "" ||
      [profileData.firstName, profileData.lastName]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      offer.memberName?.trim() ||
      "" ||
      "Creator"
    : offer.memberName?.trim() || "" || "Creator";

  const backPath = `/o/${orgSlug}/offers/${offerId}?memberId=${memberId}`;

  return (
    <LandingPageProvider
      organizationId={organization.id}
      profileId={profileId}
      projectId={`standalone-${offerId}`}
      interactionId={offerId}
      affiliateOfferPromoId={offer.affiliateOfferPromoId}
      offerId={offer.offerId}
      secureUrl={offerUrl}
      clientId={offer.clientId}
      memberId={String(memberId)}
      backPath={backPath}
      profileName={creatorName}
    >
      <LandingPageEditor />
    </LandingPageProvider>
  );
}

function LoadingSkeleton() {
  return (
    <div className="flex flex-col min-h-full">
      <div className="border-b px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Skeleton className="h-5 w-48 mb-1.5" />
            <Skeleton className="h-3.5 w-64" />
          </div>
        </div>
      </div>
      <div className="flex-1 px-4 py-6 sm:px-6">
        <Skeleton className="h-full w-full rounded-lg" />
      </div>
    </div>
  );
}
